/**
 * HeartRateZonesCard — iter1057 — максимален пулс (Tanaka), зони по пулсов
 * резерв (Karvonen) и възстановяване на пулса от 6-минутния тест.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { ArrowRight, Gauge, Loader2 } from "lucide-react";

const ZONE_COLORS = { z1: "#38BDF8", z2: "#10B981", z3: "#84CC16", z4: "#F59E0B", z5: "#EF4444" };

export const HeartRateZonesCard = ({ isDark, refreshKey }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/cardio/zones`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [API, refreshKey]);

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const box = isDark ? "bg-slate-800/50" : "bg-slate-100";

  if (loading) {
    return <div className={`p-5 rounded-2xl border ${card} flex justify-center`} data-testid="zones-loading">
      <Loader2 className="w-5 h-5 animate-spin text-red-500" /></div>;
  }
  if (!data) return null;

  return (
    <div className={`p-5 rounded-2xl border ${card}`} data-testid="hr-zones-card">
      <div className="flex items-center gap-2 mb-1">
        <Gauge className="w-4 h-4 text-red-500" />
        <h2 className="font-bold text-sm">Пулсов резерв и зони</h2>
        {data.max_hr && (
          <span className="ml-auto text-[11px] px-2 py-1 rounded-md font-bold bg-red-600 text-white" data-testid="max-hr">
            макс. {data.max_hr.used}
          </span>
        )}
      </div>

      {data.max_hr?.note_bg && (
        <p className="text-[11px] opacity-75 leading-relaxed mb-2" data-testid="max-hr-note">{data.max_hr.note_bg}</p>
      )}

      {data.zones ? (
        <>
          <div className={`text-[10px] p-2 rounded-lg mb-2 ${box}`} data-testid="zones-basis">
            Резерв {data.zones.reserve_bpm} удара (макс. {data.zones.max_hr} − покой {data.zones.resting_hr})
            {data.zones.basis === "assumed_rest" && " · пулсът в покой е приет за 70, впиши истинския за точни зони"}
          </div>
          <div className="space-y-1.5" data-testid="zones-list">
            {data.zones.zones.map((z) => (
              <div key={z.key} className={`p-2 rounded-lg flex items-center gap-2 ${box}`} data-testid={`zone-${z.key}`}>
                <span className="w-1.5 h-8 rounded-full shrink-0" style={{ background: ZONE_COLORS[z.key] }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-bold">{z.label_bg} <span className="opacity-50 font-normal">· {z.pct_bg}</span></div>
                  <div className="text-[10px] opacity-75 leading-snug">{z.text_bg}</div>
                </div>
                <span className="text-xs font-bold tabular-nums shrink-0">{z.low_bpm}–{z.high_bpm}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] opacity-60 mt-2" data-testid="karvonen-formula">{data.zones.formula_bg}</p>
        </>
      ) : (
        <p className="text-xs opacity-80" data-testid="zones-empty">
          За зоните трябват възраст в профила и пулс в покой.
        </p>
      )}

      {data.recovery && (
        <div className={`mt-3 p-3 rounded-xl ${data.recovery.band === "poor"
          ? (isDark ? "bg-rose-900/25 border border-rose-500/40" : "bg-rose-50 border border-rose-200")
          : (isDark ? "bg-emerald-900/20 border border-emerald-500/30" : "bg-emerald-50 border border-emerald-200")}`}
             data-testid="hr-recovery">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold tabular-nums" data-testid="hr-recovery-delta">−{data.recovery.delta_bpm}</span>
            <span className="text-[11px] font-bold">{data.recovery.label_bg}</span>
          </div>
          <div className="text-[11px] opacity-85 mt-1 leading-relaxed">{data.recovery.text_bg}</div>
          {data.recovery.source_bg && (
            <button onClick={() => navigate(data.recovery.route)} data-testid="hr-recovery-source"
                    className="text-[10px] opacity-70 hover:opacity-100 underline mt-1">
              {data.recovery.source_bg}
            </button>
          )}
        </div>
      )}

      {data.shock_index != null && (
        <div className={`mt-2 p-2 rounded-lg text-[11px] ${box}`} data-testid="shock-index">
          <span className="font-bold">Шоков индекс {data.shock_index}</span> · {data.shock_band.label_bg}
        </div>
      )}

      <p className="text-[10px] opacity-65 mt-2">{data.note_bg}</p>

      {data.missing_bg?.length > 0 && (
        <p className="text-[10px] opacity-70 mt-1" data-testid="zones-missing">
          Липсва: {data.missing_bg.join("; ")}.
        </p>
      )}

      {data.links?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="zones-links">
          {data.links.map((l, i) => (
            <button key={i} onClick={() => navigate(l.route)} data-testid={`zones-link-${i}`}
                    className={`text-[10px] px-2 py-1 rounded-lg border flex items-center gap-1 ${isDark ? "border-slate-700" : "border-slate-300"}`}>
              {l.twin_bg} <ArrowRight className="w-3 h-3" /> <span className="opacity-70">{l.why_bg}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default HeartRateZonesCard;
